import { useEffect, useState } from "react";
import { useSearchParams } from "react-router-dom";
import { Phone, Mail, MapPin, Clock, CheckCircle2 } from "lucide-react";
import { toast } from "sonner";
import { Layout } from "@/components/Layout";
import { COMPANY, INQUIRY_TYPES } from "@/lib/company";
import { submitContact } from "@/lib/api";

const fieldCls =
  "w-full rounded-md border border-slate-300 bg-white px-3 py-2.5 text-sm text-slate-800 focus:border-blue-700 focus:outline-none focus:ring-1 focus:ring-blue-700";

export default function Contact() {
  const [params] = useSearchParams();
  const presetType = params.get("type");
  const presetSubject = params.get("subject") || "";
  const presetMessage = params.get("message") || "";
  const stock = params.get("stock") || "";

  const [form, setForm] = useState({
    name: "", email: "", phone: "",
    inquiry_type: INQUIRY_TYPES.includes(presetType) ? presetType : INQUIRY_TYPES[2],
    subject: presetSubject, message: presetMessage,
  });
  const [submitting, setSubmitting] = useState(false);
  const [done, setDone] = useState(false);

  useEffect(() => {
    if (stock) {
      setForm((f) => ({
        ...f,
        subject: f.subject || `Inquiry about vehicle #${stock}`,
        message: f.message || `I'm interested in vehicle stock #${stock}. Please share more details.`,
      }));
    }
  }, [stock]);

  const update = (k) => (e) => setForm((f) => ({ ...f, [k]: e.target.value }));

  const submit = async (e) => {
    e.preventDefault();
    if (!form.name || !form.email || !form.phone || !form.subject || !form.message) {
      toast.error("Please fill in all required fields.");
      return;
    }
    setSubmitting(true);
    try {
      await submitContact({ ...form, stock_number: stock || null });
      setDone(true);
      toast.success("Your message has been sent. We'll be in touch shortly.");
      setForm({ name: "", email: "", phone: "", inquiry_type: form.inquiry_type, subject: "", message: "" });
    } catch {
      toast.error("Something went wrong sending your message. Please call us directly.");
    } finally {
      setSubmitting(false);
    }
  };

  return (
    <Layout>
      <div className="bg-slate-900" data-testid="contact-header">
        <div className="mx-auto max-w-7xl px-4 py-12">
          <h1 className="text-3xl sm:text-4xl font-extrabold tracking-tight text-white">Contact Us</h1>
          <p className="mt-2 text-slate-300 max-w-2xl">
            Reach out for a vehicle purchase inquiry, a transportation quote, or any general
            question. We respond during business hours.
          </p>
        </div>
      </div>

      <div className="mx-auto max-w-7xl px-4 py-14 grid grid-cols-1 lg:grid-cols-[1fr_360px] gap-10">
        {/* Form */}
        <div>
          {done ? (
            <div className="border border-green-200 bg-green-50 rounded-md p-8 text-center" data-testid="contact-success">
              <CheckCircle2 className="h-10 w-10 text-green-600 mx-auto" />
              <h2 className="mt-3 text-xl font-bold text-slate-900">Message Sent</h2>
              <p className="mt-2 text-slate-600">Thank you for contacting {COMPANY.name}. We'll respond as soon as possible.</p>
              <button onClick={() => setDone(false)} className="mt-5 rounded-md bg-blue-800 px-5 py-2.5 text-sm font-semibold text-white hover:bg-blue-900" data-testid="contact-send-another">
                Send another message
              </button>
            </div>
          ) : (
            <form onSubmit={submit} className="space-y-5" data-testid="contact-form">
              <div>
                <label className="block text-sm font-medium text-slate-700 mb-1.5">Inquiry Type</label>
                <select value={form.inquiry_type} onChange={update("inquiry_type")} className={fieldCls} data-testid="contact-inquiry-type-select">
                  {INQUIRY_TYPES.map((t) => <option key={t} value={t}>{t}</option>)}
                </select>
              </div>

              <div className="grid grid-cols-1 sm:grid-cols-2 gap-5">
                <div>
                  <label className="block text-sm font-medium text-slate-700 mb-1.5">Name <span className="text-red-600">*</span></label>
                  <input value={form.name} onChange={update("name")} className={fieldCls} data-testid="contact-name-input" />
                </div>
                <div>
                  <label className="block text-sm font-medium text-slate-700 mb-1.5">Phone <span className="text-red-600">*</span></label>
                  <input value={form.phone} onChange={update("phone")} className={fieldCls} data-testid="contact-phone-input" />
                </div>
              </div>

              <div>
                <label className="block text-sm font-medium text-slate-700 mb-1.5">Email <span className="text-red-600">*</span></label>
                <input type="email" value={form.email} onChange={update("email")} className={fieldCls} data-testid="contact-email-input" />
              </div>

              <div>
                <label className="block text-sm font-medium text-slate-700 mb-1.5">Subject <span className="text-red-600">*</span></label>
                <input value={form.subject} onChange={update("subject")} className={fieldCls} data-testid="contact-subject-input" />
              </div>

              <div>
                <label className="block text-sm font-medium text-slate-700 mb-1.5">Message <span className="text-red-600">*</span></label>
                <textarea value={form.message} onChange={update("message")} rows={6} className={fieldCls} data-testid="contact-message-input" />
              </div>

              <button type="submit" disabled={submitting} className="inline-flex items-center justify-center rounded-md bg-blue-800 px-6 py-3 text-sm font-semibold text-white hover:bg-blue-900 disabled:opacity-60" data-testid="contact-submit-btn">
                {submitting ? "Sending…" : "Send Message"}
              </button>
            </form>
          )}
        </div>

        {/* Details */}
        <aside className="space-y-4" data-testid="contact-details">
          <div className="border border-slate-200 rounded-md p-6 bg-white">
            <h2 className="font-bold text-slate-900">{COMPANY.name}</h2>
            <div className="mt-4 space-y-4 text-sm">
              <div className="flex gap-3">
                <MapPin className="h-5 w-5 text-blue-800 shrink-0" />
                <address className="not-italic text-slate-700">
                  {COMPANY.address_line1}<br />{COMPANY.address_line2}<br />{COMPANY.country}
                </address>
              </div>
              <div className="flex gap-3 items-center">
                <Phone className="h-5 w-5 text-blue-800 shrink-0" />
                <a href={`tel:${COMPANY.phoneHref}`} className="text-slate-700 hover:text-blue-800">{COMPANY.phone}</a>
              </div>
              <div className="flex gap-3 items-center">
                <Mail className="h-5 w-5 text-blue-800 shrink-0" />
                <a href={`mailto:${COMPANY.email}`} className="text-slate-700 hover:text-blue-800 break-all">{COMPANY.email}</a>
              </div>
              <div className="flex gap-3 items-center">
                <Clock className="h-5 w-5 text-blue-800 shrink-0" />
                <span className="text-slate-700">{COMPANY.hours}</span>
              </div>
            </div>
          </div>

          <div className="border border-slate-200 rounded-md p-6 bg-slate-50">
            <h3 className="font-semibold text-slate-900 text-sm">Inquiry Options</h3>
            <ul className="mt-3 space-y-2 text-sm text-slate-600">
              {INQUIRY_TYPES.map((t) => <li key={t}>• {t}</li>)}
            </ul>
          </div>
        </aside>
      </div>
    </Layout>
  );
}
